import {
  Button,
  Megaphone,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
} from "@ews-admin/global-design-system";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ConfirmationModal } from "../../components/molecules/ConfirmationModal/ConfirmationModal";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  clearError,
  createAppNoticeRequest,
  deleteAppNoticeRequest,
  fetchAppNoticesRequest,
  selectAppNoticeDeleting,
  selectAppNoticeLoading,
  selectAppNoticeRules,
  selectAppNoticeSaving,
  updateAppNoticeRequest,
} from "../../store/reducers/appNoticeSlice";
import {
  AppNoticeDto,
  NoticeTypeEnum,
  AppNoticeInput,
  toPayload,
} from "../../types/appNotice.definition";
import { formatDateTime } from "../../utils/date-format-utils";
import { appNoticeScopeLabel } from "../../utils/enum-label-utils";
import { AppConfigRuleModal } from "./AppConfigRuleModal";

const DUPLICATE_ERROR_CODE = "APP_CONFIG_DUPLICATE_RULE";

export const AnnouncementConfig: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const rules = useAppSelector(selectAppNoticeRules);
  const isLoading = useAppSelector(selectAppNoticeLoading);
  const isSaving = useAppSelector(selectAppNoticeSaving);
  const isDeleting = useAppSelector(selectAppNoticeDeleting);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<AppNoticeDto | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [inlineError, setInlineError] = useState<string | null>(null);

  // Show only ANNOUNCEMENT rules on this page. Filter client-side because the
  // store may also hold MAINTENANCE rules if the maintenance page was visited.
  const announcementRules = useMemo(
    () => rules.filter(r => r.featureType === NoticeTypeEnum.ANNOUNCEMENT),
    [rules]
  );

  useEffect(() => {
    dispatch(
      fetchAppNoticesRequest({
        featureType: NoticeTypeEnum.ANNOUNCEMENT,
      })
    );
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  const handleRefresh = () =>
    dispatch(
      fetchAppNoticesRequest({
        featureType: NoticeTypeEnum.ANNOUNCEMENT,
      })
    );

  const openCreate = () => {
    setEditingRule(null);
    setInlineError(null);
    setIsFormOpen(true);
  };

  const openEdit = (rule: AppNoticeDto) => {
    setEditingRule(rule);
    setInlineError(null);
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setEditingRule(null);
    setInlineError(null);
  };

  const handleSubmit = (input: AppNoticeInput) => {
    setInlineError(null);
    const payload = toPayload({
      ...input,
      featureType: NoticeTypeEnum.ANNOUNCEMENT,
    });

    const onError = (code: string) => {
      if (code === DUPLICATE_ERROR_CODE) {
        setInlineError(t("admin.governance.announcement.duplicateError"));
      } else {
        toast.error(t("admin.governance.announcement.toast.saveError"));
      }
    };

    if (editingRule) {
      dispatch(
        updateAppNoticeRequest({
          id: editingRule.id,
          data: payload,
          onSuccess: () => {
            closeForm();
            toast.success(t("admin.governance.announcement.toast.updateOk"));
          },
          onError,
        })
      );
    } else {
      dispatch(
        createAppNoticeRequest({
          data: payload,
          onSuccess: () => {
            closeForm();
            toast.success(t("admin.governance.announcement.toast.createOk"));
          },
          onError,
        })
      );
    }
  };

  const handleDelete = () => {
    if (deletingId === null) return;
    const id = deletingId;
    setDeletingId(null);
    dispatch(
      deleteAppNoticeRequest({
        id,
        onSuccess: () => {
          toast.success(t("admin.governance.announcement.toast.deleteOk"));
        },
        onError: () => {
          toast.error(t("admin.governance.announcement.toast.deleteError"));
        },
      })
    );
  };

  // MED-1516: the list printed the raw stored scope ("qa", "tchad") while the
  // rule form's own dropdowns showed the label ("QA", "Tchad"). Same mapping,
  // one source.
  const renderScope = (
    kind: "environments" | "countries",
    value: string | null
  ) =>
    value === null ? (
      <span className="text-neutral-500 italic">
        {t("admin.governance.scope.all")}
      </span>
    ) : (
      <span className="text-xs px-2 py-0.5 rounded bg-neutral-100 text-neutral-700">
        {appNoticeScopeLabel(t, kind, value)}
      </span>
    );

  return (
    <div className="page-container">
      {/* ── Header ───────────────────────────────────────────────────────── */}
      <div className="flex justify-between items-center page-header">
        <div>
          <h2 className="text-2xl font-bold text-neutral-900">
            {t("admin.governance.announcement.title")}
          </h2>
          <p className="text-neutral-600">
            {t("admin.governance.announcement.subtitle")}
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={isLoading}
            className="flex items-center space-x-2"
          >
            <RefreshCw
              className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`}
            />
            <span>{t("common.refresh")}</span>
          </Button>
          <Button onClick={openCreate} className="flex items-center space-x-2">
            <Plus className="w-4 h-4" />
            <span>{t("admin.governance.announcement.newRule")}</span>
          </Button>
        </div>
      </div>

      {/* ── Table ────────────────────────────────────────────────────────── */}
      <div className="page-section">
        {isLoading ? (
          <div className="py-12 text-center">
            <RefreshCw className="mx-auto w-8 h-8 animate-spin text-neutral-400" />
            <p className="mt-2 text-sm text-neutral-500">
              {t("common.loading")}
            </p>
          </div>
        ) : announcementRules.length === 0 ? (
          <div className="py-12 text-center">
            <Megaphone className="mx-auto w-12 h-12 text-neutral-400" />
            <h3 className="mt-2 text-sm font-medium text-neutral-900">
              {t("admin.governance.announcement.empty")}
            </h3>
            <p className="mt-1 text-sm text-neutral-500">
              {t("admin.governance.announcement.emptyHint")}
            </p>
            <div className="mt-6">
              <Button onClick={openCreate}>
                <Plus className="mr-2 w-4 h-4" />
                {t("admin.governance.announcement.newRule")}
              </Button>
            </div>
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-neutral-200">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 border-b border-neutral-200">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-neutral-600">
                    {t("admin.governance.table.environment")}
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-neutral-600">
                    {t("admin.governance.table.country")}
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-neutral-600">
                    {t("admin.governance.table.enabled")}
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-neutral-600">
                    {t("admin.governance.table.validFrom")}
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-neutral-600">
                    {t("admin.governance.table.validUntil")}
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-neutral-600 w-32">
                    {t("common.actions")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {announcementRules.map(rule => (
                  <tr
                    key={rule.id}
                    className="hover:bg-neutral-50 transition-colors"
                  >
                    <td className="px-4 py-3">
                      {renderScope("environments", rule.environment)}
                    </td>
                    <td className="px-4 py-3">
                      {renderScope("countries", rule.country)}
                    </td>
                    <td className="px-4 py-3">
                      {rule.enabled ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-success-50 text-success-700">
                          {t("admin.governance.status.enabled")}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-neutral-100 text-neutral-600">
                          {t("admin.governance.status.disabled")}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-neutral-700">
                      {formatDateTime(rule.validFrom, true)}
                    </td>
                    <td className="px-4 py-3 text-neutral-700">
                      {formatDateTime(rule.validUntil, true)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end items-center space-x-1">
                        <button
                          onClick={() => openEdit(rule)}
                          className="p-1.5 rounded text-neutral-600 hover:bg-neutral-100 hover:text-ews-primary transition-colors"
                          title={t("common.edit")}
                          aria-label={t("common.edit")}
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDeletingId(rule.id)}
                          className="p-1.5 rounded text-neutral-600 hover:bg-danger-50 hover:text-danger-600 transition-colors"
                          title={t("common.delete")}
                          aria-label={t("common.delete")}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <AppConfigRuleModal
        isOpen={isFormOpen}
        onClose={closeForm}
        onSubmit={handleSubmit}
        featureType={NoticeTypeEnum.ANNOUNCEMENT}
        initial={editingRule}
        isSaving={isSaving}
        inlineError={inlineError}
      />

      <ConfirmationModal
        isOpen={deletingId !== null}
        onClose={() => setDeletingId(null)}
        onConfirm={handleDelete}
        title={t("admin.governance.announcement.deleteConfirm.title")}
        message={t("admin.governance.announcement.deleteConfirm.message")}
        variant="danger"
        isLoading={isDeleting}
        confirmLabel={t("common.delete")}
      />
    </div>
  );
};
